import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import { Box, Spinner } from '@indeed/ifl-components';
import { useAutoApplyFreeTextPreferencesQuery } from '../components/__generated__/FreeTextPreferencesSection.generated';
import FreeTextPreferenceEditDialog from './FreeTextPreferenceEditDialog';
import GettextFactory from '../../../util/GettextFactory';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import type { FlowHistoryState } from '../../../lib/pageFlow';

/** Route the native JSMABridge webview modal loads to edit the free-text preference. */
export const FREE_TEXT_PREFERENCE_EDIT_PATH = '/auto-apply-setup/free-text-preference/edit';

// TODO(IAFE-3601): replace with the real mutation.
const MOCK_SUBMIT_DELAY_MS = 1000;

const mockSubmit = (): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, MOCK_SUBMIT_DELAY_MS);
    });

/**
 * Standalone page rendered inside a native JSMABridge webview modal. It reuses
 * {@link FreeTextPreferenceEditDialog} (full screen to fill the webview) and
 * dismisses the modal once the update succeeds.
 *
 * The text area is prefilled from `autoApplyInfo.freeTextPreference.text` (the job
 * seeker's text, or the service-provided blurb). The native modal is its own page
 * load with its own Apollo cache, so this fetches with the same query as
 * FreeTextPreferencesSection.
 */
const FreeTextPreferenceEditPage: React.FC = () => {
    const gt = GettextFactory();
    const { goBack } = usePageFlow();
    const location = useLocation();
    // Opened in-app (web, or the native-bridge fallback) via enterSubFlow, which records
    // the origin; a native modal loads this page cold, so there is none. In-app navigation
    // can happen inside the native webview too, so JSMABridge.isAvailable() alone can't
    // tell whether this page is a modal.
    const openedInWeb = !!(location.state as FlowHistoryState | null)?.subFlowOrigin;
    const { data, loading } = useAutoApplyFreeTextPreferencesQuery({ fetchPolicy: 'cache-first' });

    const handleSubmit = async () => {
        await mockSubmit();
        if (!openedInWeb && JSMABridge.isAvailable()) {
            void JSMABridge.dismissModalWithWebView({ result: 'updated' });
        } else {
            // Opened as a sub-flow detour from FreeTextPreferencesSection, so go back to it.
            void goBack();
        }
    };

    return (
        <>
            <Helmet>
                <title>{gt.gettext('Match preferences')}</title>
            </Helmet>
            {/* Spinner only on the initial load: a refetch (e.g. useRefetchOnPageResume)
                keeps `data`, so the dialog stays mounted and keeps the typed text. */}
            {loading && !data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                    <Spinner title={gt.gettext('Loading preferences')} />
                </Box>
            ) : (
                // Mounted after loading: the dialog reads defaultText only on mount.
                // TODO(IAFE-3601): handle a failed load instead of starting from empty text.
                <FreeTextPreferenceEditDialog
                    defaultText={data?.autoApplyInfo?.freeTextPreference?.text ?? ''}
                    onSubmit={handleSubmit}
                    fullScreen
                    // The native modal's header shows the title; in-app the dialog does.
                    // Not based on JSMABridge.isAvailable(), which differs between SSR and client.
                    inNativeBottomSheet={!openedInWeb}
                />
            )}
        </>
    );
};

export default FreeTextPreferenceEditPage;
